import { useCallback, useState } from 'react';
import {
  useGetInstrument,
  useTradingValidations,
  useTradingMethods,
  useEditingTradingFields,
  useRealTimeChanges,
  useTradingProtection,
  useCalculatingBet,
  useRequiredMargin,
} from './hooks';
import { useInstrument } from '@trader/hooks';

const timeoutToClose = 100;

export const useTrading = () => {
  const [isInsufficientError, setIsInsufficientError] =
    useState<boolean>(false);

  const checkIsInsufficientError = (value: boolean) => {
    setIsInsufficientError(value);
  };

  const {
    instrument,
    realTimePrice,
    getInstrumentDetailFE,
    updateInstrument,
    updateRealTimePrice,
    resetInstrument,
  } = useGetInstrument();

  const { requiredMargin, getRequiredMargin, isRequiredMarginError } =
    useRequiredMargin();

  const { isEditedFields, toggleEditField, clearEditingFields } =
    useEditingTradingFields();
  const tradingProtection = useTradingProtection(instrument, realTimePrice);
  const { errors, removeProtectionErrors, removeSomeError } =
    useTradingValidations(instrument, realTimePrice, isEditedFields);
  useRealTimeChanges(
    updateInstrument,
    realTimePrice,
    isEditedFields,
    instrument
  );

  const {
    handleChangeMarket,
    handleClearProtection,
    handleMarketBillType,
    handleLimitBillType,
    handleChangeProtection,
    handleChangeStopLimit,
    switchTakeProfit,
    switchStopLoss,
    handleChangeUnit,
    handleChangePips,
  } = useTradingMethods(
    instrument,
    realTimePrice,
    toggleEditField,
    updateInstrument,
    removeProtectionErrors,
    clearEditingFields,
    removeSomeError
  );

  const handleStartTrading = useCallback((state, type) => {
    getInstrumentDetailFE(state, type);
  }, []);

  useInstrument(
    instrument.general.symbol,
    instrument.general.spreadDiff,
    updateRealTimePrice
  );

  // clear some part of context related field changing after close create modal
  const clearPreviousState = () => {
    setTimeout(() => {
      resetInstrument();
      clearEditingFields();
    }, timeoutToClose);
  };

  return {
    handleStartTrading,
    updateRealTimePrice,
    instrument,
    realTimePrice,
    isEditedFields,
    toggleEditField,
    errors,
    handleChangeMarket,
    handleChangeProtection,
    handleChangeStopLimit,
    handleMarketBillType,
    handleLimitBillType,
    handleClearProtection,
    switchTakeProfit,
    switchStopLoss,
    removeProtectionErrors,
    tradingProtection,
    useCalculatingBet,
    clearPreviousState,
    isInsufficientError,
    checkIsInsufficientError,
    requiredMargin,
    getRequiredMargin,
    isRequiredMarginError,
    handleChangeUnit,
    handleChangePips,
  };
};

export type TUseTrading = ReturnType<typeof useTrading>;
